import React, { useState } from 'react'
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown'
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp'

import { Icon } from '@/shared/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'
import { ContentBlock } from '@/slices/blog/mocks/articles'
import { extractHeadings } from '@/slices/blog/utils/generateHeadingId'
import { Box, Collapse, styled, useMediaQuery, useTheme } from '@mui/material'

interface InlineTableOfContentsProps {
  blocks: ContentBlock[]
}

export const InlineTableOfContents: React.FC<InlineTableOfContentsProps> = ({ blocks }) => {
  const headings = extractHeadings(blocks)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [open, setOpen] = useState(isDesktop)

  if (headings.length < 3) {
    return null
  }

  return (
    <TocWrapper>
      <TocHeader onClick={() => setOpen(!open)}>
        <TocLabel>In this article</TocLabel>
        <TocToggle>
          <Icon icon={open ? faChevronUp : faChevronDown} />
        </TocToggle>
      </TocHeader>
      <Collapse in={open}>
        <TocList>
          {headings.map(({ id, text }) => (
            <TocItem key={id}>
              <TocLink href={`#${id}`}>{text}</TocLink>
            </TocItem>
          ))}
        </TocList>
      </Collapse>
    </TocWrapper>
  )
}

const TocWrapper = styled(Box)(({ theme }) => ({
  marginBottom: pxToRem(32),
  paddingTop: pxToRem(20),
  paddingBottom: pxToRem(20),
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(40),
    paddingTop: pxToRem(24),
    paddingBottom: pxToRem(24),
  },
}))

const TocHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  userSelect: 'none',
})

const TocLabel = styled('span')(({ theme }) => ({
  fontSize: pxToRem(13),
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: theme.palette.primary.main,
}))

const TocToggle = styled(Box)(({ theme }) => ({
  fontSize: pxToRem(12),
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  transition: 'color 0.2s ease',

  '&:hover': {
    color: theme.palette.primary.main,
  },
}))

const TocList = styled('ol')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  marginTop: pxToRem(16),
  counterReset: 'toc-counter',

  [theme.breakpoints.up('md')]: {
    marginTop: pxToRem(20),
  },
}))

const TocItem = styled('li')(({ theme }) => ({
  counterIncrement: 'toc-counter',
  marginBottom: pxToRem(8),

  '&:last-child': {
    marginBottom: 0,
  },

  '&::before': {
    content: 'counter(toc-counter) "."',
    color: theme.palette.text.disabled,
    fontSize: pxToRem(13),
    fontWeight: 500,
    marginRight: pxToRem(8),
  },
}))

const TocLink = styled('a')(({ theme }) => ({
  fontSize: pxToRem(14),
  fontWeight: 400,
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  transition: 'color 0.2s ease',
  lineHeight: 1.5,

  '&:hover': {
    color: theme.palette.custom.terracotta,
  },

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(15),
  },
}))
